import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useEffect } from 'react';

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  severity?: 'warning' | 'error' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  severity = 'warning',
  onConfirm,
  onCancel,
}) => {
  // Add keyboard shortcut for Enter key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (open && event.key === 'Enter') {
        event.preventDefault();
        onConfirm();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onConfirm]);

  const getSeverityColor = () => {
    switch (severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      default:
        return '#ff9800';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          border: '1px solid rgba(85, 203, 205, 0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        pb: 2, 
        pt: 3,      
        px: 3
      }}>
        <Warning sx={{ 
          color: getSeverityColor(),
          width: '25px',
          height: '22px'
        }} />
        <Typography 
          variant="h6" 
          component="span"
          sx={{ 
            fontWeight: 600,
            fontFamily: 'Soria, serif',
            fontSize: '1.4em',
            color: '#2c3e50'
          }}
        >
          {title}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0, px: 3, pb: 1 }}>
        <Typography 
          variant="body1"
          sx={{ 
            lineHeight: 1.7,
            fontSize: '1em',
            color: '#34495e',
            fontFamily: 'Poppins, sans-serif'
          }}
        >
          {message}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
        <Button 
          onClick={onCancel} 
          variant="outlined"
          sx={{          
            minWidth: 100,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95em',
            fontWeight: 500,
            borderColor: 'rgba(85, 203, 205, 0.3)',
            color: '#55cbcd',     
            '&:hover': {            
              borderColor: '#55cbcd',
              backgroundColor: 'rgba(85, 203, 205, 0.05)'      
            }
          }}
        >
          {cancelText}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained"
          sx={{          
            minWidth: 100,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.95em',
            fontWeight: 500,
            backgroundColor: '#fcb9aa', // Coral color
            '&:hover': {   
              backgroundColor: '#f8a99a' // Darker coral on hover
            }
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog; 